<script lang="ts" setup>
// @ts-ignore
import { Splide } from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css'

export interface ArticlesProps {
    options?: any
}

const props = withDefaults(defineProps<ArticlesProps>(), {
    options: {
        pagination: true,
        arrows: true,
        gap: '3rem',
        drag: false,
        perPage: 3,
        breakpoints: {
            992: {
                drag: true,
                pagination: true,
                gap: '2rem',
                arrows: true,
            },
            768: {
                fixedWidth: '90%',
                perPage: 1,
                gap: '1rem',
            },
        },
    },
})

const { $t } = useNuxtApp()

// Splide i18n: Slovak translations for arrow and pagination labels
const i18n = {
    prev: $t('Predchádzajúci') as string,
    next: $t('Nasledujúci') as string,
    first: $t('Prejsť na prvú snímku') as string,
    last: $t('Prejsť na poslednú snímku') as string,
    slideX: $t('Prejsť na snímku %s') as string,
    pageX: $t('Prejsť na stránku %s') as string,
    play: $t('Spustiť automatické prehrávanie') as string,
    pause: $t('Pozastaviť automatické prehrávanie') as string,
    carousel: $t('Karusel') as string,
    select: $t('Vyberte snímku na zobrazenie') as string,
    slide: $t('snímka') as string,
    slideLabel: $t('%s z %s') as string,
}

const mergedOptions = computed(() => ({
    ...props.options,
    i18n,
}))

// Accessibility: Remove Splide's default carousel ARIA attributes (WCAG 1.3.1)
// Splide applies carousel semantics (role="group", aria-roledescription="slide") that conflict
// with semantic content inside slides (articles, cards). We remove these to let the
// content's native semantics take precedence.
const handleMounted = (splide: any) => {
    // Remove carousel role from root
    const splideRoot = splide.root
    if (splideRoot) {
        splideRoot.removeAttribute('role')
        splideRoot.removeAttribute('aria-roledescription')
    }

    // Remove live region from track (prevents announcement spam during navigation)
    const track = splide.Components.Elements.track
    if (track) {
        track.removeAttribute('aria-live')
        track.removeAttribute('aria-atomic')
    }

    // Remove role from the list element
    const list = splide.Components.Elements.list
    if (list) {
        list.removeAttribute('role')
    }

    // Remove carousel semantics from slides - let the content inside define semantics
    splide.Components.Elements.slides.forEach((slide: HTMLElement) => {
        slide.removeAttribute('role')
        slide.removeAttribute('aria-roledescription')
        slide.removeAttribute('aria-label')
    })

    // WCAG 2.4.3: Fix focus order by moving arrows after the track in the DOM
    // By default Splide renders arrows before the track, which creates incorrect tab order
    const arrows = splideRoot.querySelector('.splide__arrows')
    const pagination = splideRoot.querySelector('.splide__pagination')
    if (arrows && track) {
        // Move arrows after the track
        track.after(arrows)
    }

    // Ensure pagination comes after arrows (or after track if no arrows)
    if (pagination) {
        if (arrows) {
            arrows.after(pagination)
        } else if (track) {
            track.after(pagination)
        }
    }
}
</script>

<template>
  <Splide
    :options="mergedOptions"
    @splide:mounted="handleMounted"
  >
    <slot />
  </Splide>
</template>
<style lang="scss">
@import './Slider.scss';
</style>
